// Touch layout (§8.3, §14.5, §14.6). Pure geometry: given the viewport, safe-area insets and the
// touch settings, it returns where every control sits. `touch.ts` renders it; tests assert it.
//
// Landscape (spec): BONK 96 px, 24 px from the right and bottom edges; JUMP 72 px centered
// 128 px left of and 88 px above BONK; GRAB 72 px directly left of BONK (12 px gap); ITEM 64 px
// above BONK (12 px gap); HONK 48 px top-right below the kill feed with EMOTE 48 px to its left;
// PAUSE top-left. Move stick: anywhere in the left 45 % below the top 64 px.
// Portrait (spec): buttons stay in the bottom-right quadrant; the stick zone is the bottom-left
// 50 % x 45 %. The landscape cluster is too wide for half a phone, so portrait uses a 2x2 grid
// ([JUMP][ITEM] over [GRAB][BONK]), shrunk if needed to fit the quadrant.
// All sizes are CSS px x UI scale x button size (S/M/L 0.85/1.0/1.2). Every hit target is at
// least 48 px (§14.5) even when the drawn disc is smaller (PAUSE is drawn at 44 px).
// Left-handed mirrors the action cluster and the stick zone only: PAUSE stays top-left and
// HONK/EMOTE stay under the kill feed (top-right), because those follow the HUD, not the thumb.
// `hud` tells the HUD where its widgets fit without covering a control (Dizzy Dial, mode widget).

export type TouchButtonId = 'shove' | 'jump' | 'grab' | 'item' | 'honk' | 'emote' | 'pause' | 'items';

export interface Rect { x: number; y: number; w: number; h: number }
/** A round control: center, drawn radius and hit radius (≥ 24 px). */
export interface TouchCircle { x: number; y: number; r: number; hit: number }

export interface TouchLayoutInput {
  width: number;
  height: number;
  safe?: { top: number; right: number; bottom: number; left: number };
  /** UI-scale setting as a factor (0.8–1.4). */
  uiScale?: number;
  buttonSize?: 'S' | 'M' | 'L';
  leftHanded?: boolean;
  /** Height reserved under the top edge for the kill feed (HONK/EMOTE go below it). */
  killFeedHeight?: number;
}

export interface TouchLayout {
  orientation: 'landscape' | 'portrait';
  /** Final scale applied to the spec sizes (after fitting). */
  scale: number;
  mirrored: boolean;
  buttons: Record<TouchButtonId, TouchCircle>;
  /** Where a touch spawns the move stick. */
  stickZone: Rect;
  /** Fixed-stick center, also where the idle "ghost" stick is drawn. */
  stickHome: { x: number; y: number };
  stickRadius: number;
  knobRadius: number;
  /** Bounding box of the action cluster (BONK/JUMP/GRAB/ITEM), for HUD avoidance. */
  cluster: Rect;
  /** Bounding box of the top-right HONK/EMOTE pair. */
  topCluster: Rect;
  /** Room the HUD can use without covering a control (CSS px). */
  hud: TouchHudHints;
}

export interface TouchHudHints {
  /** The top-left mode widget starts right of PAUSE: its left edge (x). */
  topLeftX: number;
  /**
   * Bottom-center widget (the Dizzy Dial): its center x, the distance its bottom edge keeps from
   * the viewport bottom, and the widest it may be there.
   */
  bottomCenter: { cx: number; bottom: number; maxWidth: number };
}

export const MIN_TARGET = 48;
export const BUTTON_SIZE_MULT: Readonly<Record<'S' | 'M' | 'L', number>> = { S: 0.85, M: 1, L: 1.2 };

/** Spec sizes (diameters) and offsets, CSS px at scale 1 (§8.3). */
export const TOUCH_SPEC = {
  bonk: 96, jump: 72, grab: 72, item: 64, honk: 48, emote: 48, pause: 44, items: 48,
  edge: 24, gap: 12, jumpDx: 128, jumpDy: 88,
  stickBase: 60, stickKnob: 28, stickDeadzone: 0.1, stickTop: 64, stickZoneLandscape: 0.45,
  portraitZoneW: 0.5, portraitZoneH: 0.45, topMargin: 12,
} as const;

/** Kill feed reservation under the top edge: 5 lines, or 3 below 400 px viewport height (§14.3). */
export function defaultKillFeedHeight(height: number): number {
  return height < 400 ? 78 : 136;
}

function circle(x: number, y: number, diameter: number): TouchCircle {
  const r = diameter / 2;
  return { x, y, r, hit: Math.max(r, MIN_TARGET / 2) };
}

function bounds(cs: readonly TouchCircle[]): Rect {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const c of cs) {
    x0 = Math.min(x0, c.x - c.hit);
    y0 = Math.min(y0, c.y - c.hit);
    x1 = Math.max(x1, c.x + c.hit);
    y1 = Math.max(y1, c.y + c.hit);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

interface Cluster { bonk: TouchCircle; jump: TouchCircle; grab: TouchCircle; item: TouchCircle }

/** Landscape cluster anchored at the bottom-right corner (right-handed coordinates). */
function landscapeCluster(right: number, bottom: number, s: number): Cluster {
  const t = TOUCH_SPEC;
  const bx = right - t.edge * s - (t.bonk * s) / 2;
  const by = bottom - t.edge * s - (t.bonk * s) / 2;
  return {
    bonk: circle(bx, by, t.bonk * s),
    jump: circle(bx - t.jumpDx * s, by - t.jumpDy * s, t.jump * s),
    grab: circle(bx - (t.bonk / 2 + t.gap + t.grab / 2) * s, by, t.grab * s),
    item: circle(bx, by - (t.bonk / 2 + t.gap + t.item / 2) * s, t.item * s),
  };
}

/** Portrait 2x2 grid: [JUMP][ITEM] over [GRAB][BONK]; GRAB and JUMP share a column left of BONK. */
function portraitCluster(right: number, bottom: number, s: number, edge: number): Cluster {
  const t = TOUCH_SPEC;
  const bx = right - edge - (t.bonk * s) / 2;
  const by = bottom - edge - (t.bonk * s) / 2;
  const gx = bx - (t.bonk / 2 + t.gap + t.grab / 2) * s;
  return {
    bonk: circle(bx, by, t.bonk * s),
    grab: circle(gx, by, t.grab * s),
    item: circle(bx, by - (t.bonk / 2 + t.gap + t.item / 2) * s, t.item * s),
    jump: circle(gx, by - (t.grab / 2 + t.gap + t.jump / 2) * s, t.jump * s),
  };
}

function mirrorCircle(c: TouchCircle, width: number): TouchCircle {
  return { ...c, x: width - c.x };
}

function mirrorRect(r: Rect, width: number): Rect {
  return { ...r, x: width - r.x - r.w };
}

/** Computes the full touch layout for a viewport (§8.3). */
export function computeTouchLayout(inp: TouchLayoutInput): TouchLayout {
  const t = TOUCH_SPEC;
  const W = inp.width;
  const H = inp.height;
  const safe = inp.safe ?? { top: 0, right: 0, bottom: 0, left: 0 };
  const leftHanded = inp.leftHanded === true;
  const orientation: TouchLayout['orientation'] = W >= H ? 'landscape' : 'portrait';
  const base = (inp.uiScale ?? 1) * BUTTON_SIZE_MULT[inp.buttonSize ?? 'M'];
  // The cluster and stick are computed right-handed with the side insets swapped, then flipped.
  const insetR = leftHanded ? safe.left : safe.right;
  const insetL = leftHanded ? safe.right : safe.left;
  const right = W - insetR;
  const left = insetL;
  const bottom = H - safe.bottom;
  const top = safe.top;

  // Top-right pair (HONK, EMOTE) under the kill feed, PAUSE top-left: fixed size, never shrunk,
  // never mirrored.
  const feed = inp.killFeedHeight ?? defaultKillFeedHeight(H);
  const topS = Math.max(0.85, Math.min(base, 1.2));
  const topHit = Math.max(t.honk * topS, MIN_TARGET);
  const honkY = top + feed + t.topMargin + topHit / 2;
  const honkX = W - safe.right - t.gap - topHit / 2;
  const honk = circle(honkX, honkY, t.honk * topS);
  const emote = circle(honkX - topHit - t.gap, honkY, t.emote * topS);
  // Practice item wheel: left of EMOTE on the same row.
  const items = circle(emote.x - topHit - t.gap, honkY, t.items * topS);
  const pauseD = t.pause * topS;
  const pauseHit = Math.max(pauseD, MIN_TARGET);
  const pause = circle(safe.left + t.gap + pauseHit / 2, top + t.gap + pauseHit / 2, pauseD);
  const topLimit = honkY + topHit / 2 + t.gap;

  let s = base;
  let cluster: Cluster;
  if (orientation === 'landscape') {
    // Shrink when the cluster would reach into the stick zone or up into the HONK row.
    const zoneRight = left + (W - insetL - insetR) * t.stickZoneLandscape;
    for (;;) {
      cluster = landscapeCluster(right, bottom, s);
      const b = bounds(Object.values(cluster));
      if ((b.x >= zoneRight + 8 && b.y >= topLimit) || s <= 0.6) break;
      s -= 0.02;
    }
  } else {
    // Portrait: the cluster must stay inside the bottom-right quadrant.
    const qx = W / 2 + 4;
    const qy = Math.max(H / 2, topLimit);
    for (;;) {
      cluster = portraitCluster(right, bottom, s, Math.max(16, t.edge * s * 0.75));
      const b = bounds(Object.values(cluster));
      if ((b.x >= qx && b.y >= qy) || s <= 0.6) break;
      s -= 0.02;
    }
  }

  let stickZone: Rect;
  if (orientation === 'landscape') {
    const zw = (W - insetL - insetR) * t.stickZoneLandscape;
    stickZone = { x: left, y: top + t.stickTop, w: zw, h: bottom - (top + t.stickTop) };
  } else {
    const zw = W * t.portraitZoneW - insetL;
    const zh = (H - safe.bottom) * t.portraitZoneH;
    stickZone = { x: left, y: bottom - zh, w: zw, h: zh };
  }
  // The stick scales with the settings but always fits its zone with a margin, so the idle
  // stick is fully on screen and never reaches the buttons.
  const margin = { x: orientation === 'landscape' ? 40 : 24, y: orientation === 'landscape' ? 36 : 32 };
  const stickRadius = Math.max(36, Math.min(
    t.stickBase * Math.max(0.85, Math.min(base, 1.3)),
    (stickZone.w - margin.x - 8) / 2,
    (stickZone.h - margin.y - 8) / 2,
  ));
  const knobRadius = stickRadius * (t.stickKnob / t.stickBase);
  const stickHome = {
    x: Math.min(stickZone.x + margin.x + stickRadius, stickZone.x + stickZone.w - stickRadius - 8),
    y: bottom - margin.y - stickRadius,
  };

  let clusterRect = bounds([cluster.bonk, cluster.jump, cluster.grab, cluster.item]);
  // Bottom-center HUD room: between the idle stick and the cluster in landscape; above both in
  // portrait (they fill the bottom edge there).
  const gap = t.gap;
  let bottomCenter: TouchHudHints['bottomCenter'];
  if (orientation === 'landscape') {
    const l = stickHome.x + stickRadius + gap;
    const r = clusterRect.x - gap;
    bottomCenter = { cx: (l + r) / 2, bottom: safe.bottom + 8, maxWidth: Math.max(0, r - l) };
  } else {
    const top = Math.min(clusterRect.y, stickHome.y - stickRadius);
    bottomCenter = { cx: W / 2, bottom: H - top + gap, maxWidth: W - insetL - insetR - 2 * gap };
  }
  if (leftHanded) {
    for (const k of ['bonk', 'jump', 'grab', 'item'] as const) cluster[k] = mirrorCircle(cluster[k], W);
    stickZone = mirrorRect(stickZone, W);
    // The mirrored zone sits under the HONK/EMOTE row: start it below that row.
    const below = topLimit;
    if (stickZone.y < below) stickZone = { ...stickZone, y: below, h: Math.max(0, stickZone.y + stickZone.h - below) };
    stickHome.x = W - stickHome.x;
    clusterRect = mirrorRect(clusterRect, W);
    bottomCenter = { ...bottomCenter, cx: W - bottomCenter.cx };
  }
  const buttons: Record<TouchButtonId, TouchCircle> = {
    shove: cluster.bonk, jump: cluster.jump, grab: cluster.grab, item: cluster.item, honk, emote, pause, items,
  };
  return {
    orientation, scale: s, mirrored: leftHanded, buttons, stickZone, stickHome, stickRadius, knobRadius,
    cluster: clusterRect, topCluster: bounds([honk, emote]),
    hud: { topLeftX: pause.x + pause.hit + gap, bottomCenter },
  };
}

/** True when two hit circles overlap (touching edges do not count). */
export function circlesOverlap(a: TouchCircle, b: TouchCircle): boolean {
  return Math.hypot(a.x - b.x, a.y - b.y) < a.hit + b.hit - 0.01;
}

export function circleRect(c: TouchCircle): Rect {
  return { x: c.x - c.hit, y: c.y - c.hit, w: c.hit * 2, h: c.hit * 2 };
}

export function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w - 0.01 && b.x < a.x + a.w - 0.01 && a.y < b.y + b.h - 0.01 && b.y < a.y + a.h - 0.01;
}

/**
 * The floating stick (§8.3): displacement from the spawn point, clamped to the base radius,
 * with a 10 % radial deadzone rescaled to 0..1. Writes the move vector into `out` (screen axes:
 * +y is down, which is sim +y per §2.1).
 */
export function stickVector(dx: number, dy: number, radius: number, out: { x: number; y: number }, deadzone: number = TOUCH_SPEC.stickDeadzone): { x: number; y: number } {
  const m = Math.hypot(dx, dy);
  const n = m / radius;
  if (n <= deadzone || m === 0) {
    out.x = 0;
    out.y = 0;
    return out;
  }
  const mag = Math.min(1, (n - deadzone) / (1 - deadzone));
  out.x = (dx / m) * mag;
  out.y = (dy / m) * mag;
  return out;
}
